import { z, ZodSchema } from "zod";
import {
  PurposeEnum,
  TypeEnum,
  ApartmentCategoryEnum,
  HouseCategoryEnum,
  LandCategoryEnum,
  OfficeCategoryEnum,
  ListingTypeEnum,
  BuildingTypeEnum,
  OfficeTypeEnum,
} from "@/types/types";

// --- Enums & Sub-Schemas ---

const ZodPurposeEnum = z.enum([PurposeEnum.FORSALE, PurposeEnum.FORRENT]);
const ZodTypeEnum = z.enum([
  TypeEnum.APARTMENT,
  TypeEnum.HOUSE,
  TypeEnum.LAND,
  TypeEnum.OFFICE,
]);

const ZodApartmentCategoryEnum = z.enum(
  Object.values(ApartmentCategoryEnum) as [string, ...string[]],
  { errorMap: () => ({ message: "apartmentCategoryError" }) },
);
const ZodHouseCategoryEnum = z.enum(
  Object.values(HouseCategoryEnum) as [string, ...string[]],
  { errorMap: () => ({ message: "houseCategoryError" }) },
);
const ZodLandCategoryEnum = z.enum(
  Object.values(LandCategoryEnum) as [string, ...string[]],
  { errorMap: () => ({ message: "landCategoryError" }) },
);
const ZodOfficeCategoryEnum = z.enum(
  Object.values(OfficeCategoryEnum) as [string, ...string[]],
  { errorMap: () => ({ message: "officeCategoryError" }) },
);

// const ZodBuildingTypeEnum = z.nativeEnum(BuildingTypeEnum);
// const ZodOfficeTypeEnum = z.nativeEnum(OfficeTypeEnum);

// Validate the MediaItem map
const MediaItemSchema = z.object({
  MediaId: z.string(),
  Status: z.enum(["UPLOADED", "PROCESSING", "READY", "FAILED", "REJECTED"]),
  OriginalFileName: z.string(),
  MediaCategory: z.enum(["image", "video", "document"]),
  FileExtension: z.string(),
});

// --- Main Schema Generator ---

export const getDraftValidationSchema = (
  type: TypeEnum,
  purpose: PurposeEnum,
  listing_type: ListingTypeEnum,
): ZodSchema<any> => {
  // Determine Tier Limits
  let maxPhotos = 20;
  let maxDescLength = 2000;
  let canHaveVideo = false;

  if (listing_type === ListingTypeEnum.PLUS) {
    maxPhotos = 40;
    maxDescLength = 2500;
    canHaveVideo = true;
  } else if (listing_type === ListingTypeEnum.PRIME) {
    maxPhotos = 60;
    maxDescLength = 3000;
    canHaveVideo = true;
  } else if (listing_type === ListingTypeEnum.SPOTLIGHT) {
    maxPhotos = 80;
    maxDescLength = 3500;
    canHaveVideo = true;
  }

  const currentYear = new Date().getFullYear();

  // ==========================================
  // 1. BASIC INFORMATION
  // ==========================================
  let schema: any = z.object({
    listing_id: z.string().min(10, "invalidListingId"),
    purpose: ZodPurposeEnum,
    type: ZodTypeEnum,
  });

  // Dynamic Categories & Building Types
  if (type === TypeEnum.APARTMENT) {
    schema = schema.extend({
      category: ZodApartmentCategoryEnum,
    });
  } else if (type === TypeEnum.HOUSE) {
    schema = schema.extend({ category: ZodHouseCategoryEnum });
  } else if (type === TypeEnum.OFFICE) {
    schema = schema.extend({
      category: ZodOfficeCategoryEnum,
    });
  } else if (type === TypeEnum.LAND) {
    schema = schema.extend({ category: ZodLandCategoryEnum });
  }

  // ==========================================
  // 2. LOCATION & MAP
  // ==========================================
  schema = schema.extend({
    map_location: z
      .object({
        lat: z.number().min(-90).max(90),
        long: z.number().min(-180).max(180),
      })
      .nullable()
      .refine((data) => data !== null, { message: "locationNotSet" }),
  });

  // ==========================================
  // 3. PRICING & CONTENT
  // ==========================================
  schema = schema.extend({
    title: z.string().min(20, "titleTooShort").max(100, "titleTooLong"),
    price: z.number().min(1, "priceTooLow"),
    description: z
      .string()
      .min(200, "descriptionTooShort")
      .max(maxDescLength, "descriptionTooLong"),
    ai_notes: z
      .string()
      .max(1000, "aiNotesTooLong")
      .optional()
      .or(z.literal("")),
  });

  // ==========================================
  // 4. PROPERTY SPECS
  // ==========================================
  if (type === TypeEnum.LAND) {
    // Land Specs
    schema = schema.extend({
      lot_size: z.number().min(1, "lotSizeTooLow"),
    });
    if (purpose === PurposeEnum.FORSALE) {
      schema = schema.extend({
        buyers_fee: z
          .number()
          .min(0, "buyersFeeTooLow")
          .max(30, "buyersFeeTooHigh"),
      });
    }
  } else {
    // Structural Specs (Apartment, House, Office)

    // Selectors (Only for Appt & House)
    if (type === TypeEnum.APARTMENT || type === TypeEnum.HOUSE) {
      schema = schema.extend({
        number_of_rooms: z.number().min(1, "roomsTooLow"),
        number_of_bedrooms: z.number().min(1, "bedroomsTooLow"),
        number_of_full_bathrooms: z.number().min(0, "fullBathroomsInvalid"), // Full baths can be 0
        number_of_bathrooms: z.number().min(1, "bathroomsTooLow"),
      });
    }

    // Area & Dimensions
    let structExtensions: any = {
      interior_space: z.number().min(1, "interiorTooLow"),
      total_structure_area: z.number().min(1, "totalStructureTooLow"),
      levels: z.string().min(1, "levelsNotSet"),
      year_built: z
        .number()
        .min(1900, "yearBuiltTooOld")
        .max(currentYear + 6, "yearBuiltTooFarFuture"),
      available_from: z.string().optional().or(z.literal("")), // 🚀 ADD THIS LINE
    };

    if (type === TypeEnum.HOUSE) {
      structExtensions.lot_size = z.number().min(1, "lotSizeTooLow");
    } else if (type === TypeEnum.APARTMENT || type === TypeEnum.OFFICE) {
      structExtensions.lot_size = z.number().optional();
    }

    if (purpose === PurposeEnum.FORSALE) {
      structExtensions.buyers_fee = z
        .number()
        .min(0, "buyersFeeTooLow")
        .max(30, "buyersFeeTooHigh");
    }

    structExtensions.construction_materials = z
      .string()
      .optional()
      .or(z.literal(""));
    structExtensions.reference_id = z.string().optional().or(z.literal(""));

    schema = schema.extend(structExtensions);
  }

  // ==========================================
  // 5. MEDIA & TOURS
  // ==========================================
  schema = schema.extend({
    photos: z
      .record(z.string(), MediaItemSchema)
      .refine(
        (data) => {
          const readyCount = Object.values(data).length;
          return readyCount >= 3;
        },
        { message: "notEnoughPhotos" },
      )
      .refine((data) => Object.keys(data).length <= maxPhotos, {
        message: "tooManyPhotos",
      }),

    // 🚀 FIX: Check for an actual MediaId instead of Object.keys
    presentation_video: z.any().refine(
      (val) => {
        // If they don't have video tier access, but they HAVE a valid MediaId, fail validation
        if (!canHaveVideo && val && val.MediaId && val.MediaId.trim() !== "") {
          return false;
        }
        return true;
      },
      { message: "videoNotAllowedOnTier" },
    ),

    web_tour_url: z.string().url("urlInvalid").optional().or(z.literal("")),

    // 🚀 FIX: Prevent brochure on essential tier if a MediaId exists
    brochure: z.any().refine(
      (val) => {
        // Essential tier cannot have a brochure either
        const canHaveBrochure =
          listing_type === ListingTypeEnum.PRIME ||
          listing_type === ListingTypeEnum.SPOTLIGHT;
        if (
          !canHaveBrochure &&
          val &&
          val.MediaId &&
          val.MediaId.trim() !== ""
        ) {
          return false;
        }
        return true;
      },
      { message: "brochureNotAllowedOnTier" }, // Ensure you have this in your translation files!
    ),
  });

  // ==========================================
  // 6. FACILITIES & AMENITIES
  // ==========================================
  if (type !== TypeEnum.LAND) {
    schema = schema.extend({
      orientation: z
        .object({
          north: z.boolean().default(false),
          south: z.boolean().default(false),
          east: z.boolean().default(false),
          west: z.boolean().default(false),
        })
        .optional()
        // Ensure this fallback exists if it's undefined
        .default({ north: false, south: false, east: false, west: false }),
    });
  }

  // ==========================================
  // 7. AGENCY
  // ==========================================
  schema = schema.extend({
    is_agency_listing: z.boolean().optional(),
    agents_ids: z
      .array(z.string())
      .max(15, "tooManyAgentsAssigned")
      .nullable()
      .optional(),
  });

  // ==========================================
  // 8. CROSS-FIELD REFINEMENTS (Evaluated Last)
  // ==========================================
  return (
    schema
      .refine(
        (data: any) => {
          // If it's a structural type and year_built is future, available_from MUST exist
          if (
            data.type !== TypeEnum.LAND &&
            data.year_built > currentYear &&
            !data.available_from
          ) {
            return false;
          }
          return true;
        },
        { message: "availableFromRequired", path: ["available_from"] },
      )
      // 🚀 1. NEW: Check if available_from year matches year_built
      .refine(
        (data: any) => {
          if (
            data.type !== TypeEnum.LAND &&
            data.year_built > currentYear &&
            data.available_from
          ) {
            const availableYear = new Date(data.available_from).getFullYear();
            return availableYear === data.year_built;
          }
          return true;
        },
        { message: "availableFromYearMismatch", path: ["available_from"] },
      )
      // 🚀 2. NEW: Check if current floor is NOT strictly greater than total floors
      .refine(
        (data: any) => {
          if (
            (data.type === TypeEnum.APARTMENT ||
              data.type === TypeEnum.OFFICE) &&
            data.levels
          ) {
            const parts = data.levels.split("/");
            if (parts.length === 2) {
              // Trim handles the " / " spacing you used in your component
              const currentFloor = parseInt(parts[0].trim(), 10);
              const totalFloors = parseInt(parts[1].trim(), 10);

              if (!isNaN(currentFloor) && !isNaN(totalFloors)) {
                return currentFloor <= totalFloors;
              }
            }
          }
          return true;
        },
        { message: "currentFloorExceedsTotal", path: ["levels"] },
      )
      .refine(
        (data: any) => {
          // Max 2 orientations Check
          if (data.type !== TypeEnum.LAND && data.orientation) {
            const count = Object.values(data.orientation).filter(
              Boolean,
            ).length;
            return count <= 2;
          }
          return true;
        },
        { message: "max2Orientations", path: ["orientation"] },
      )
  );
};
